import { useState } from "react";
import { useGame } from "../../src/stores/useGame";
import { useDialogs } from "../stores/useDialogs";
import type { DialogItem } from "../types/Dialog.types";
import { shuffleAnswers } from "../utils/shuffleAnswers";
import * as THREE from "three";

const defaultDialogItem: DialogItem = {
  facts: ["This NPC doesn't have proper dialog data."],
  question: "Continue?",
  answers: ["Yes", "No"],
  correctAnswerIndex: 0,
  answerForIncorrect: "Please check the dialog format."
};

interface UsePersonInteractionProps {
  npcId: string;
  dialogIdToLoad: string;
  npcName: string;
  mapId: string;
  isUnlocked: boolean;
  onComplete?: () => void;
  rigidBodyRef: React.RefObject<any>;
}

export const usePersonInteraction = ({
  npcId,
  dialogIdToLoad,
  npcName,
  mapId,
  isUnlocked,
  onComplete,
  rigidBodyRef
}: UsePersonInteractionProps) => {
  const [isFetchingDialog, setIsFetchingDialog] = useState(false);
  
  const dialog = useDialogs((state) => state.dialog);
  const openDialog = useDialogs((state) => state.openDialog);
  const setCameraTarget = useGame((state) => state.setCameraTarget);
  const setCameraPos = useGame((state) => state.setCameraPos);
  const playerPosition = useGame((state) => state.playerPosition);

  const getCameraPositions = () => {
    if (!rigidBodyRef.current || !playerPosition) return { pos: { x: 0, y: 0, z: 0 }, target: { x: 0, y: 0, z: 0 } };
    const currentPos = rigidBodyRef.current.translation();
    
    // Calculate direction from player to NPC
    const direction = {
      x: currentPos.x - playerPosition.x,
      y: 0, // Keep horizontal only
      z: currentPos.z - playerPosition.z
    };
    
    // Normalize direction
    const length = Math.sqrt(direction.x * direction.x + direction.z * direction.z);
    if (length === 0) return { pos: { x: 0, y: 0, z: 0 }, target: { x: 0, y: 0, z: 0 } };
    
    const normalizedDirection = {
      x: direction.x / length,
      z: direction.z / length
    };
    
    // Camera position: 1 unit away from Person in the direction from player to Person
    const cameraPos = {
      x: currentPos.x - normalizedDirection.x * 2, // 2 units away
      y: currentPos.y + 1, // Camera height
      z: currentPos.z - normalizedDirection.z * 2
    };
    
    // Camera target: Person position
    const cameraTarget = {
      x: currentPos.x,
      y: currentPos.y + 1,
      z: currentPos.z
    };
    
    return { pos: cameraPos, target: cameraTarget };
  };

  const handleStartInteraction = async () => {
    if (isFetchingDialog || !isUnlocked) return;
    
    // Check if dialog is already open
    if (dialog.isOpen) return;
    try {
      setIsFetchingDialog(true);
      
      const res = await fetch(`/data/dialogs/${dialogIdToLoad}.json`);
      const data = await res.json().then(data => data.dialog) as DialogItem[];

      // Set camera positions for dialog
      const { pos, target } = getCameraPositions();
      setCameraPos(pos);
      setCameraTarget(target);
      
      // Check if data is in the expected array format
      let dialogSections = data;
      if (!Array.isArray(data)) {
        console.warn(`Dialog data for ${dialogIdToLoad} is not in the expected format. Using default dialog.`);
        dialogSections = [defaultDialogItem];
      }
      
      // Shuffle answers for each section
      dialogSections = shuffleAnswers(dialogSections);
      
      // Open dialog with the dialog sections
      openDialog({
        npcId,
        mapId,
        npcName: npcName || "Character",
        dialogSections: dialogSections,
        onComplete: onComplete,
      });
    } catch (e) {
      // Set camera positions for dialog
      const { pos, target } = getCameraPositions();
      setCameraPos(pos);
      setCameraTarget(target);
      
      // Use default dialog for error case
      openDialog({
        npcId,
        mapId,
        npcName: npcName || "Character",
        dialogSections: [defaultDialogItem],
        onComplete: onComplete,
      });
    } finally {
      setIsFetchingDialog(false);
    }
  };

  return {
    isFetchingDialog,
    handleStartInteraction,
    dialogIsOpen: dialog.isOpen
  };
};

